import { useCallback, useEffect, useRef, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { useCarrier } from '../../contexts/CarrierContext';
import { InspectionSettingsProvider } from '../../contexts/InspectionSettingsContext';
import OversightTab from '../admin/OversightTab';
import CarrierSwitcher from '../carrier/CarrierSwitcher';
import CarrierBadge from '../carrier/CarrierBadge';
import '../../styles/AdminPage.css';
import { supabase } from '../../lib/supabaseClient';
import { installerAdminApi } from '../../utils/installerApi';
import { jobTitle } from '../../shared/installationProcess';
import JobDefectsReview from '../admin/installers/JobDefectsReview';
import '../../styles/InstallerAdmin.css';
import WorkspaceSwitcher from '../WorkspaceSwitcher';
import SiteVisits from './SiteVisits';
import QualityAudits from './QualityAudits';
import FollowupQueue from './FollowupQueue';
import CivilAreasSubtab from '../admin/installers/CivilAreasSubtab';
import ReviewQueue from './ReviewQueue';
import ReviewScreen from './ReviewScreen';
import './supervision.css';

async function getToken() { const { data: { session } } = await supabase.auth.getSession(); return session?.access_token; }

// «ناظر» (supervisor) surface: review the AI-found defects of completed+inspected
// jobs and approve / reject them. Reuses the admin JobDefectsReview modal; the
// supervisor cannot manage users, jobs, or settings.
function LegacyReview() {
  const [jobs, setJobs] = useState([]);
  const [err, setErr] = useState(null);
  const [defectsJobId, setDefectsJobId] = useState(null);

  const controller = useRef(null);
  const load = useCallback(async () => {
    const signal = controller.current.signal;
    try {
      const token = await getToken();
      if (signal.aborted) return;
      const r = await installerAdminApi.jobs(token, 'completed');
      if (signal.aborted) return;
      setJobs((r.jobs || []).filter((j) => j.inspection_id));
      setErr(null);
    } catch (e) { if (!signal.aborted && e.name !== 'AbortError') setErr(e.message); }
  }, []);
  useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    load();
    return () => current.abort();
  }, [load]);

  return (
    <div className="iadmin" dir="rtl">
      <h2 className="iadmin-head">بازبینیِ ناظر</h2>
      <p className="iadmin-muted">کارهای تکمیل‌شده‌ای که بازرسیِ هوش مصنوعی روی آن‌ها اجرا شده؛ ایرادهای «رفع‌شده» را تأیید یا رد کنید.</p>
      {err && <div role="alert" className="iadmin-alert">{err}</div>}
      {jobs.length === 0 && !err && <p className="iadmin-muted">کاری برای بازبینی نیست.</p>}
      <div className="iadmin-list">
        {jobs.map((j) => (
          <button key={j.id} className="iadmin-rowcard" onClick={() => setDefectsJobId(j.id)}>
            <span className="iadmin-rowmain">
              <span className="iadmin-rowname">{jobTitle(j)}</span>
              <span className="iadmin-rowmeta">{j.customer_address_text}</span>
            </span>
            <svg className="iadmin-chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
          </button>
        ))}
      </div>
      {defectsJobId && <JobDefectsReview jobId={defectsJobId} onClose={() => { setDefectsJobId(null); load(); }} />}
    </div>
  );
}


export default function SupervisorPage() {
  const { profile, signOut } = useAuth();
  const { confirmLeave, carrier } = useCarrier();
  const location = useLocation();
  const navigate = useNavigate();
  const active = location.pathname.startsWith('/supervisor/oversight') ? 'oversight'
    : location.pathname === '/supervisor/civil-areas' ? 'civil-areas' : location.pathname === '/supervisor/visits' ? 'visits' : location.pathname === '/supervisor/quality-audits' ? 'audits' : location.pathname === '/supervisor/followups' ? 'followups' : location.pathname === '/supervisor/legacy' ? 'legacy' : 'review';
  return <div className="admin-root" dir="rtl">
    <div className="admin-bar">
      <span className="admin-bar-brand">پنل ناظر</span>
      <CarrierSwitcher />
      <span className="admin-bar-rail"><span className="admin-bar-user">{profile?.full_name || 'ناظر'}</span><WorkspaceSwitcher current="supervisor" /></span>
      <button type="button" className="admin-bar-exit" onClick={async () => {
        if (!await confirmLeave()) return;
        await signOut(); navigate('/login', { replace: true });
      }}>خروج</button>
    </div>
    <Tabs.Root value={active} dir="rtl" onValueChange={async (value) => {
      const path = value === 'civil-areas' ? '/supervisor/civil-areas' : value === 'visits' ? '/supervisor/visits' : value === 'audits' ? '/supervisor/quality-audits' : value === 'followups' ? '/supervisor/followups' : value === 'oversight' ? '/supervisor/oversight/council' : value === 'legacy' ? '/supervisor/legacy' : '/supervisor';
      if (await confirmLeave()) navigate(`${path}${location.search}`);
    }}>
      <div className="admin-tabs-row">
        <Tabs.List className="admin-tabs" aria-label="بخش‌های پنل ناظر">
          <Tabs.Trigger className="admin-tab" value="review">صف بازبینی</Tabs.Trigger>
          <Tabs.Trigger className="admin-tab" value="visits">بازدیدهای ارجاعی</Tabs.Trigger>
          <Tabs.Trigger className="admin-tab" value="audits">بازبینی نمونه‌ای</Tabs.Trigger>
          <Tabs.Trigger className="admin-tab" value="followups">پیگیری‌های اصلاح</Tabs.Trigger>
          <Tabs.Trigger className="admin-tab" value="civil-areas">مناطق اجرا</Tabs.Trigger>
          <Tabs.Trigger className="admin-tab" value="legacy">ایرادهای کارهای قدیمی</Tabs.Trigger>
          <Tabs.Trigger className="admin-tab" value="oversight">بازرسی آزاد</Tabs.Trigger>
        </Tabs.List>
        {active === 'oversight' && <CarrierBadge className="carrier-context-tag" srPrefix="اپراتور فعال:" />}
      </div>
      <div className="admin-body">
        <Tabs.Content value="review"><Routes>
          <Route path="review/:jobStageId" element={<ReviewScreen key={`${carrier?.id}:${location.pathname}`} />} />
          <Route path="*" element={<ReviewQueue key={carrier?.id || 'unselected'} />} />
        </Routes></Tabs.Content>
        <Tabs.Content value="visits"><SiteVisits key={carrier?.id || 'unselected'} /></Tabs.Content>
        <Tabs.Content value="audits"><QualityAudits key={carrier?.id || 'unselected'} /></Tabs.Content>
        <Tabs.Content value="followups"><FollowupQueue key={carrier?.id || 'unselected'} /></Tabs.Content>
        <Tabs.Content value="civil-areas"><CivilAreasSubtab key={carrier?.id || 'unselected'} role={profile?.role} /></Tabs.Content>
        <Tabs.Content value="legacy"><LegacyReview key={carrier?.id || 'unselected'} /></Tabs.Content>
        <Tabs.Content value="oversight" forceMount hidden={active !== 'oversight'} className="oversight-tab-content">
          <InspectionSettingsProvider>
            <Routes>
              <Route path="oversight/:sub?" element={<OversightTab role={profile?.role} basePath="/supervisor/oversight" />} />
              <Route path="*" element={<OversightTab role={profile?.role} basePath="/supervisor/oversight" />} />
            </Routes>
          </InspectionSettingsProvider>
        </Tabs.Content>
      </div>
    </Tabs.Root>
  </div>;
}
